import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import type { CatalogoDePermisos } from '@/shared/api/tipos';

interface Props {
  catalogo: CatalogoDePermisos;
  marcados: Set<number>;
  alAlternar: (id: number) => void;
  alAlternarModulo: (ids: number[], marcarTodos: boolean) => void;
}

export function SelectorDePermisos({ catalogo, marcados, alAlternar, alAlternarModulo }: Props) {
  const [filtro, setFiltro] = useState('');

  const total = useMemo(() => Object.values(catalogo).flat().length, [catalogo]);

  const visibles = useMemo(() => {
    const texto = filtro.trim().toLowerCase();
    if (!texto) {
      return Object.entries(catalogo);
    }
    return Object.entries(catalogo)
      .map(([modulo, permisos]) => {

        const moduloCoincide = modulo.replace(/_/g, ' ').includes(texto);
        const filtrados = moduloCoincide
          ? permisos
          : permisos.filter(
              (p) => p.nombre.toLowerCase().includes(texto) || p.slug.toLowerCase().includes(texto),
            );
        return [modulo, filtrados] as const;
      })
      .filter(([, permisos]) => permisos.length > 0);
  }, [catalogo, filtro]);

  return (
    <div className="grid gap-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 size-[15px] -translate-y-1/2 text-armazon-400"
            aria-hidden
          />
          <input
            type="search"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Filtrar permisos…"
            aria-label="Filtrar permisos"
            className="h-11 w-full rounded-lg border border-armazon-200 bg-white pl-9 pr-3 text-sm text-armazon-900 outline-none transition-colors placeholder:text-armazon-400 focus:border-marca-400 focus:ring-2 focus:ring-marca-500/20"
          />
        </div>
        <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-armazon-500">
          {marcados.size} de {total}
        </span>
      </div>

      <div className="max-h-80 overflow-y-auto rounded-lg border border-armazon-200">
        {visibles.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-armazon-500">
            Ningún permiso coincide con «{filtro}»
          </p>
        ) : (
          visibles.map(([modulo, permisos]) => {
            const ids = permisos.map((p) => p.id);
            const todosMarcados = ids.every((id) => marcados.has(id));

            return (
              <fieldset key={modulo} className="border-b border-armazon-100 last:border-b-0">
                <legend className="sr-only">{modulo.replace(/_/g, ' ')}</legend>

                <div className="sticky top-0 flex items-center justify-between bg-armazon-50 px-4 py-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-armazon-700">
                    {modulo.replace(/_/g, ' ')}
                  </span>
                  <button
                    type="button"
                    onClick={() => alAlternarModulo(ids, !todosMarcados)}
                    className="text-[11px] font-semibold uppercase tracking-wide text-marca-600 transition-colors hover:text-marca-700"
                  >
                    {todosMarcados ? 'Ninguno' : 'Todos'}
                  </button>
                </div>

                {permisos.map((p) => (
                  <label
                    key={p.id}
                    className="flex cursor-pointer items-center gap-2.5 px-4 py-2 transition-colors hover:bg-armazon-50"
                  >
                    <input
                      type="checkbox"
                      checked={marcados.has(p.id)}
                      onChange={() => alAlternar(p.id)}
                      className="size-4 shrink-0 accent-marca-600"
                    />
                    <span className="min-w-0 flex-1 truncate text-sm uppercase text-armazon-700">
                      {p.nombre}
                    </span>
                    <span className="shrink-0 rounded bg-armazon-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-armazon-600">
                      {p.accion}
                    </span>
                  </label>
                ))}
              </fieldset>
            );
          })
        )}
      </div>
    </div>
  );
}
